import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ICON } from '@attendance/ui';
import { IconButton } from '@attendance/ui/components/IconButton';
import { SearchBox } from '../common/SearchBox.js';
import { SearchResults } from './SearchResults.js';
import { useGlobalSearch } from './useGlobalSearch.js';
import { openCommandPalette, registerHeaderSearch } from './useCommandPaletteOpen.js';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
const LIST_ID = 'header-search-listbox';
const optionId = (index: number): string => `header-search-option-${index}`;

/**
 * THE SEARCH IN THE APP BAR — a field you type into, in place (owner,
 * 2026-10-12: "search field should get wider and write in field, not opening a
 * new popup").
 *
 * It was a button that opened the ⌘K palette over the page. Now the bar's free
 * middle IS the field, up to `max-w-header-search`, and its results drop down
 * under it — pages and records, the same `useGlobalSearch` the palette runs, so
 * the two cannot answer the same words differently. Ctrl/⌘+K focuses it.
 *
 * AN ICON UNTIL IT IS WANTED (owner, 2026-10-13: "reduce width — only when the
 * user clicks it, get this size; maybe icon only"). The bar shows a magnifier;
 * a click or Ctrl/⌘+K opens the wide field in its place, and leaving it empty
 * folds it back. An unrendered field is also one no browser can autofill.
 *
 * ON A PHONE the bar has no middle to give: the magnifier opens the palette
 * there, which is the same search with the room a phone does have.
 */
const wideEnough = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches === true;

export function HeaderSearch(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Mounted narrow, then widened a frame later, so the width TRANSITIONS open.
  const [grown, setGrown] = useState(false);
  const [active, setActive] = useState(0);
  const search = useGlobalSearch(query, open);
  const { flat } = search;
  const activeIndex = flat.length === 0 ? -1 : Math.min(active, flat.length - 1);

  useEffect(() => setActive(0), [query]);
  // Opening the field puts the caret in it.
  useEffect(() => {
    if (!expanded) return setGrown(false);
    inputRef.current?.focus();
    const frame = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(frame);
  }, [expanded]);
  // Ctrl/⌘+K comes here when the bar has room for the field (not on a phone).
  useEffect(
    () =>
      registerHeaderSearch(() => {
        if (!wideEnough()) return false;
        setExpanded(true);
        inputRef.current?.focus();
        return true;
      }),
    []
  );

  function collapse(): void {
    setOpen(false);
    setQuery('');
    setExpanded(false);
  }

  function go(to: string): void {
    collapse();
    navigate(to);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[activeIndex];
      if (row) go(row.to);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      collapse();
    }
  }

  const label = t('command.searchPlaceholder', 'Search pages, people and records…');
  return (
    <div className="flex min-w-0 flex-1 justify-end">
      {expanded ? (
      <div
        className={`relative hidden max-w-header-search transition-[width] duration-normal ease-entrance motion-reduce:transition-none md:block ${
          grown ? 'w-full' : 'w-10'
        }`}
      >
        <SearchBox
          ref={inputRef}
          label={label}
          placeholder={label}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // A value a browser filled in on its own opens nothing: only typing does.
            if (document.activeElement === e.target) setOpen(true);
          }}
          onClear={() => setQuery('')}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            // Left empty, it folds back to the icon; with words in it, it stays.
            if (inputRef.current?.value === '') setExpanded(false);
          }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={LIST_ID}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-keyshortcuts="Control+K Meta+K"
          /* Never a login field to a browser: with a password box further down
             the page, Chrome filled the admin's saved e-mail in here and the
             search ran on it (owner, 2026-10-12). */
          name="global-search"
          autoComplete="off"
          shortcut={isMac ? '⌘K' : 'Ctrl K'}
          className="w-full"
        />
        {open && (
          /* The popover role: anchored under the field, over the page — not a
             dialog, so the page stays where it is and typing never stops. */
          <div className="absolute inset-x-0 top-full z-50 mt-1 overflow-hidden rounded-modal border border-border bg-surface shadow-popover">
            <SearchResults
              search={search}
              listId={LIST_ID}
              optionId={optionId}
              activeIndex={activeIndex}
              setActive={setActive}
              onPick={go}
            />
          </div>
        )}
      </div>
      ) : (
        <IconButton
          label={t('command.searchTitle', 'Search')}
          aria-keyshortcuts="Control+K Meta+K"
          onClick={() => (wideEnough() ? setExpanded(true) : openCommandPalette())}
        >
          <Search size={ICON.control} aria-hidden="true" />
        </IconButton>
      )}
    </div>
  );
}
